import { ComponentType, useEffect, useRef } from 'react'
import Add from '@mui/icons-material/Add'
import Check from '@mui/icons-material/Check'
import Close from '@mui/icons-material/Close'
import DoneAll from '@mui/icons-material/DoneAll'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import HourglassEmpty from '@mui/icons-material/HourglassEmpty'
import HowToReg from '@mui/icons-material/HowToReg'
import Settings from '@mui/icons-material/Settings'
import UnfoldLess from '@mui/icons-material/UnfoldLess'
import UnfoldMore from '@mui/icons-material/UnfoldMore'
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  FormControlLabel,
  IconButton,
  Popover,
  Skeleton,
  Stack,
  SvgIconProps,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import useLocalStorage from '@/hooks/useLocalStorage'
import useOverlay from '@/hooks/useOverlay'
import useViewerId from '@/hooks/useViewerId'
import { DocumentApprovalState } from '@/types/approvals'
import { getApprovalEligibility } from '@/utils/approval-actions'
import { AVATAR_SIZE } from '@/components/avatars/UserAvatar'
import ApprovalPendingPolicyList from './ApprovalPendingPolicyList'
import ApprovalPolicyProgressCard from './ApprovalPolicyProgressCard'
import ManageApprovalPoliciesDialog from './ManageApprovalPoliciesDialog'
import useDocumentApprovalActions from './useDocumentApprovalActions'

type ApprovalsNeededCardProps = {
  documentId: string
  /** When true, the card starts in a minimal view of what needs the viewer, with a Show more button for every policy. */
  collapsible?: boolean
  /** Where the Show more choice is saved, so each place the card appears remembers its own. */
  showMoreStorageKey?: string
  /** Called after an approve or reject leaves the viewer nothing to act on, if the "Move to next document" setting is on. */
  onMoveToNextDocument?: () => void
  elevation?: number
}

function LoadingSkeleton() {
  return (
    // Skeletons sit inside the same Typography variants the loaded card uses,
    // so swapping in the real content doesn't change any row heights.
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        py: 1.5,
        px: 2.5,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
        <Typography variant="subtitle1" sx={{ width: 180 }}>
          <Skeleton />
        </Typography>
        <Skeleton variant="rounded" width={64} height={24} />
      </Stack>
      <Stack sx={{ mx: -1 }}>
        {[0, 1].map((row) => (
          <Stack
            key={row}
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ py: 0.5, px: 1 }}
          >
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton
              variant="circular"
              width={AVATAR_SIZE}
              height={AVATAR_SIZE}
            />
            <Typography variant="body2" sx={{ flex: 1 }}>
              <Skeleton width="40%" />
            </Typography>
            <Skeleton variant="rounded" width={72} height={24} />
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

type ErrorPanelProps = { onRetry: () => void }

function ErrorPanel({ onRetry }: ErrorPanelProps) {
  return (
    <Stack
      alignItems="center"
      spacing={1.5}
      sx={{
        textAlign: 'center',
        py: 3,
        px: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <ErrorOutline color="error" sx={{ fontSize: 28 }} />
      <Typography variant="body2">Couldn&apos;t load approvals</Typography>
      <Button variant="outlined" size="small" onClick={onRetry}>
        Retry
      </Button>
    </Stack>
  )
}

function NoApprovalsNeeded() {
  return (
    <Stack
      alignItems="center"
      spacing={1}
      sx={{ textAlign: 'center', py: 2.5, px: 2 }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          bgcolor: 'success.main',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Check sx={{ color: 'common.white' }} />
      </Box>
      <Typography variant="body2">
        No approvals needed for this document!
      </Typography>
    </Stack>
  )
}

/** Awaiting the viewer first, then pending on others, then resolved. */
function policyBucket(
  policy: DocumentApprovalState['policies'][number],
  viewerId?: string,
): number {
  if (policy.satisfied || policy.is_rejected) return 2
  if (!viewerId) return 1

  // `canAct` alone isn't enough: it's also true for someone who already
  // decided but could still change their mind, and that's not someone the
  // card should present as awaiting action.
  const eligibility = getApprovalEligibility(policy, viewerId)
  return eligibility.canAct && eligibility.latestActionType === null ? 0 : 1
}

type SummaryTone = 'success' | 'error' | 'purple' | 'warning' | 'neutral'

type SummaryBadge = {
  tone: SummaryTone
  Icon: ComponentType<SvgIconProps>
}

type SummaryKind =
  | 'no-policies'
  | 'rejected'
  | 'all-met'
  | 'waiting-on-you'
  | 'waiting-on-others'

type Summary = {
  kind: SummaryKind
  badge: SummaryBadge
  label: string
  detail?: string
}

function StatusBadge({ tone, Icon }: SummaryBadge) {
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        flexShrink: 0,
        borderRadius: '50%',
        bgcolor: tone === 'neutral' ? 'grey.600' : `${tone}.main`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon fontSize="small" sx={{ color: 'common.white' }} />
    </Box>
  )
}

function policiesAwaitingViewer(
  approvalState: DocumentApprovalState,
  viewerId?: string,
) {
  return approvalState.policies.filter(
    (policy) => policyBucket(policy, viewerId) === 0,
  )
}

function viewerHasPolicyToAct(
  approvalState: DocumentApprovalState,
  viewerId?: string,
) {
  return policiesAwaitingViewer(approvalState, viewerId).length > 0
}

type SettingsPopoverProps = {
  moveToNextDocument: boolean
  onToggleMoveToNextDocument: () => void
}

function SettingsPopover({
  moveToNextDocument,
  onToggleMoveToNextDocument,
}: SettingsPopoverProps) {
  const overlay = useOverlay()

  return (
    <>
      <IconButton aria-label="Approvals settings" onClick={overlay.open}>
        <Settings />
      </IconButton>
      <Popover
        open={overlay.isOpen}
        onClose={overlay.close}
        anchorEl={overlay.anchorEl as HTMLButtonElement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ ml: 1.5, mr: 2.25, my: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={moveToNextDocument}
                onChange={onToggleMoveToNextDocument}
              />
            }
            label="Move to next document on approve/reject"
            sx={{ m: 0, columnGap: 1 }}
          />
        </Box>
      </Popover>
    </>
  )
}

export function getSummary(
  approvalState: DocumentApprovalState,
  viewerId?: string,
): Summary {
  const { policies } = approvalState

  if (policies.length === 0) {
    return {
      kind: 'no-policies',
      badge: { tone: 'neutral', Icon: HowToReg },
      label: 'No policies on this document',
    }
  }

  if (approvalState.is_rejected) {
    const rejectedNames = policies
      .filter((policy) => policy.is_rejected)
      .map((policy) => policy.description)
    return {
      kind: 'rejected',
      badge: { tone: 'error', Icon: Close },
      label: 'Rejected',
      detail: rejectedNames.join(', '),
    }
  }

  if (approvalState.all_policies_satisfied) {
    return {
      kind: 'all-met',
      badge: { tone: 'success', Icon: DoneAll },
      label: 'All approvals met',
    }
  }

  const satisfiedCount = policies.filter((policy) => policy.satisfied).length
  const countText =
    policies.length >= 2
      ? `${satisfiedCount} of ${policies.length} policies satisfied`
      : 'Approval pending'

  const awaitingCount = policiesAwaitingViewer(approvalState, viewerId).length
  if (awaitingCount > 0) {
    return {
      kind: 'waiting-on-you',
      badge: { tone: 'warning', Icon: HowToReg },
      label: `Waiting on you for ${awaitingCount} ${
        awaitingCount === 1 ? 'policy' : 'policies'
      }`,
    }
  }

  return {
    kind: 'waiting-on-others',
    badge: { tone: 'purple', Icon: HourglassEmpty },
    label: 'Waiting on others',
    detail: countText,
  }
}

export default function ApprovalsNeededCard({
  documentId,
  collapsible = false,
  showMoreStorageKey = 'approvals-card-show-more',
  onMoveToNextDocument,
  elevation = 4,
}: ApprovalsNeededCardProps) {
  const viewerId = useViewerId()
  const manageOverlay = useOverlay()
  const { isAuthorized: canEditPolicies } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )

  const [moveToNextDocument, setMoveToNextDocument] = useLocalStorage(
    'approvals-card-move-to-next-document',
    true,
    false,
  )

  // The card is remounted for each document. A save started on the previous
  // document can finish after the viewer has moved on. Its result must not
  // move the viewer off the document they have open now.
  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const {
    approvalState,
    isPending,
    isError,
    refetch,
    queryKey: approvalStateQueryKey,
    usersById,
    usersPending,
    handleApprove,
    handleReject,
    handleRemovePolicy,
    isRemovingPolicy,
    pendingPolicyIds,
  } = useDocumentApprovalActions({
    documentId,
    // The viewer can approve or reject for another approver. They may do
    // that on several policies in a row, so moving them to the next document
    // after the first one would cut them off. Only their own decisions move
    // them on.
    onDecisionSaved: (savedState, isOnBehalf) => {
      if (
        collapsible &&
        !isOnBehalf &&
        moveToNextDocument &&
        isMounted.current &&
        !viewerHasPolicyToAct(savedState, viewerId)
      ) {
        onMoveToNextDocument?.()
      }
    },
  })

  const policies = approvalState?.policies ?? []
  const satisfiedCount = policies.filter((policy) => policy.satisfied).length
  const sortedPolicies = [...policies].sort(
    (a, b) => policyBucket(a, viewerId) - policyBucket(b, viewerId),
  )

  const noPolicies = !isPending && !isError && policies.length === 0

  const canToggle = collapsible && !isPending && !isError && policies.length > 0

  const [showMore, setShowMore] = useLocalStorage<boolean>(
    showMoreStorageKey,
    false,
    false,
  )

  // An error always shows the card expanded, even overriding a user who had
  // collapsed it before the error happened. The retry button has to stay
  // visible.
  const expanded = !collapsible || isError || (canToggle && showMore)

  const summary =
    isPending || isError || !approvalState
      ? null
      : getSummary(approvalState, viewerId)

  const addPolicyButton = canEditPolicies && (
    <Button variant="text" startIcon={<Add />} onClick={manageOverlay.open}>
      Add policy
    </Button>
  )

  const showAddPolicyInHeader = collapsible && noPolicies

  const minimalPolicies = collapsible
    ? sortedPolicies.filter((policy) => policyBucket(policy, viewerId) === 0)
    : []

  const body = (
    <Box sx={{ pt: collapsible ? 1.5 : 0 }}>
      {isPending && <LoadingSkeleton />}
      {isError && <ErrorPanel onRetry={refetch} />}
      {!isPending &&
        !isError &&
        (policies.length === 0 ? (
          collapsible ? null : (
            <NoApprovalsNeeded />
          )
        ) : (
          <Stack spacing={1}>
            {sortedPolicies.map((policy) => (
              <ApprovalPolicyProgressCard
                key={policy.approval_policy_id}
                policy={policy}
                documentId={documentId}
                stateVersionId={approvalState?.state_version_id}
                approvalStateQueryKey={approvalStateQueryKey}
                onStaleState={refetch}
                usersById={usersById}
                usersPending={usersPending}
                onApprove={handleApprove}
                onReject={handleReject}
                onRemovePolicy={handleRemovePolicy}
                isRemovingPolicy={isRemovingPolicy}
                pendingPolicyIds={pendingPolicyIds}
              />
            ))}
          </Stack>
        ))}

      {!isPending && !isError && !showAddPolicyInHeader && addPolicyButton && (
        <Stack direction="row" sx={{ mt: 1 }}>
          {addPolicyButton}
        </Stack>
      )}
    </Box>
  )

  return (
    <Card elevation={elevation} sx={{ pointerEvents: 'all' }}>
      <CardContent
        sx={{ py: 1.5, px: collapsible ? 2 : 1.5, '&:last-child': { pb: 1.5 } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          columnGap={1.5}
          rowGap={1}
          sx={{ mb: collapsible ? 0 : 1.5 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flex: '1 1 auto', minWidth: 0 }}
          >
            {collapsible && summary?.badge && (
              <StatusBadge {...summary.badge} />
            )}
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="h6">
                {collapsible ? 'Approvals' : 'Approvals Needed'}
              </Typography>
              {collapsible && (
                <>
                  {isPending && (
                    <Typography variant="caption">
                      <Skeleton width={120} aria-label="Loading approvals" />
                    </Typography>
                  )}
                  {summary && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      <Box
                        component="span"
                        sx={
                          summary.badge.tone === 'neutral'
                            ? undefined
                            : {
                                color: `${summary.badge.tone}.main`,
                                fontWeight: 500,
                              }
                        }
                      >
                        {summary.label}
                      </Box>
                      {summary.detail && ` · ${summary.detail}`}
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flex: 'none', ml: 'auto' }}
          >
            {!collapsible && !isPending && !isError && policies.length >= 2 && (
              <Typography variant="caption" color="text.secondary">
                {satisfiedCount} of {policies.length} policies satisfied
              </Typography>
            )}
            {canToggle && (
              <Tooltip title={expanded ? 'Show less' : 'Show more'}>
                <IconButton
                  aria-label={expanded ? 'Show less' : 'Show more'}
                  aria-expanded={expanded}
                  onClick={() => setShowMore(!expanded)}
                >
                  {expanded ? <UnfoldLess /> : <UnfoldMore />}
                </IconButton>
              </Tooltip>
            )}
            {showAddPolicyInHeader && addPolicyButton}
            {/* Hidden while loading. The buttons next to it also show up
              only after loading, so they all appear together and nothing
              shifts sideways. */}
            {collapsible && !isPending && (
              <SettingsPopover
                moveToNextDocument={moveToNextDocument}
                onToggleMoveToNextDocument={() =>
                  setMoveToNextDocument((prev) => !prev)
                }
              />
            )}
          </Stack>
        </Stack>

        {collapsible ? (
          <>
            <Collapse in={expanded} unmountOnExit>
              {body}
            </Collapse>
            {!expanded && minimalPolicies.length > 0 && (
              <ApprovalPendingPolicyList
                policies={minimalPolicies}
                pendingPolicyIds={pendingPolicyIds}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            )}
          </>
        ) : (
          body
        )}

        {/* Outside the loading/error branches on purpose: a background refetch
            can fail while the dialog is open, and unmounting it there would
            throw away edits the user is part-way through. */}
        {canEditPolicies && (
          <ManageApprovalPoliciesDialog
            documentId={documentId}
            overlay={manageOverlay}
          />
        )}
      </CardContent>
    </Card>
  )
}
